/**
 * ActionDropdown — Drupal-style split action: primary visible button + caret
 * that toggles a menu of secondary actions.
 *
 * Designed for row actions where space is tight and a single inline button
 * row would crowd the layout. Closes on outside click and Escape.
 *
 * @example
 *   <ActionDropdown
 *     primary={{ label: 'Show on Map', onClick: handleShow }}
 *     items={[
 *       { label: 'Download Shapefile', href: '/node/123' },
 *       { label: 'Delete', onClick: handleDelete, danger: true },
 *     ]}
 *   />
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ActionDropdown.scss';

export interface ActionDropdownItem {
  label: React.ReactNode;
  onClick?: () => void;
  href?: string;
  target?: string;
  rel?: string;
  danger?: boolean;
  disabled?: boolean;
}

export interface ActionDropdownProps {
  primary: { label: React.ReactNode; onClick?: () => void; ariaLabel?: string; disabled?: boolean; active?: boolean };
  items: ActionDropdownItem[];
  /** Open the menu above the trigger instead of below. */
  dropUp?: boolean;
  /** Outer className passthrough so callers can scope styling. */
  className?: string;
  /** Override the size of the primary button via existing btn-sm/btn-xs modifiers. */
  size?: 'sm' | 'xs';
}

export const ActionDropdown: React.FC<ActionDropdownProps> = ({
  primary,
  items,
  dropUp = false,
  className = '',
  size = 'sm',
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Computed straight from the trigger's bounding rect — no menu-size
  // measurement needed because we anchor to the trigger's left/right edges
  // via CSS (right anchor for right-alignment, transform for dropUp).
  const [menuPos, setMenuPos] = useState<{ top: number; right: number; minWidth: number; transform: string } | null>(null);

  const close = useCallback(() => setOpen(false), []);

  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return;
    const computePos = () => {
      const rect = wrapperRef.current!.getBoundingClientRect();
      setMenuPos({
        top: dropUp ? rect.top - 4 : rect.bottom + 4,
        // Right-anchor so the menu doesn't fall off-screen when the trigger
        // sits near the viewport's right edge; alignment is against the
        // trigger's right edge.
        right: Math.max(8, window.innerWidth - rect.right),
        minWidth: rect.width,
        transform: dropUp ? 'translateY(-100%)' : 'none',
      });
    };
    computePos();
    window.addEventListener('scroll', computePos, true);
    window.addEventListener('resize', computePos);
    return () => {
      window.removeEventListener('scroll', computePos, true);
      window.removeEventListener('resize', computePos);
    };
  }, [open, dropUp]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      close();
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const activeCls = primary.active ? ' action-dropdown__primary--active' : '';

  if (items.length === 0) {
    return (
      <button
        type="button"
        className={`btn btn-${size} btn-outline${activeCls} ${className}`.trim()}
        onClick={primary.onClick}
        disabled={primary.disabled}
        aria-pressed={primary.active}
        aria-label={primary.ariaLabel}
      >
        {primary.label}
      </button>
    );
  }

  return (
    <div ref={wrapperRef} className={`action-dropdown ${dropUp ? 'action-dropdown--up' : ''} ${className}`.trim()}>
      <button
        type="button"
        className={`btn btn-${size} btn-outline action-dropdown__primary${activeCls}`}
        onClick={primary.onClick}
        disabled={primary.disabled}
        aria-pressed={primary.active}
        aria-label={primary.ariaLabel}
      >
        {primary.label}
      </button>
      <button
        type="button"
        className={`btn btn-${size} btn-outline action-dropdown__caret`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="More actions"
      >
        <span aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className={`action-dropdown__menu action-dropdown__menu--${size}`}
          role="menu"
          style={
            menuPos
              ? { top: menuPos.top, right: menuPos.right, minWidth: menuPos.minWidth, transform: menuPos.transform }
              : { visibility: 'hidden' }
          }
        >
          {items.map((item, i) => {
            const cls = `action-dropdown__item${item.danger ? ' action-dropdown__item--danger' : ''}`;
            if (item.href) {
              return (
                <a
                  key={i}
                  href={item.href}
                  target={item.target}
                  rel={item.rel}
                  className={cls}
                  role="menuitem"
                  onClick={close}
                >
                  {item.label}
                </a>
              );
            }
            return (
              <button
                key={i}
                type="button"
                className={cls}
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  item.onClick?.();
                  close();
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ActionDropdown;
